import { createContext, useContext, useState, ReactNode } from 'react'
import { useWebSocket } from './WebSocketContext'

interface A2FSettingsContextType {
  blendshapeMultipliers: Record<string, number>
  blendshapeOffsets: Record<string, number>
  faceParams: Record<string, number>
  initialEmotions: Record<string, number>
  scalars: Record<string, number | string>
  touchedCheckboxes: Set<string>
  setBlendshapeMultiplier: (key: string, val: number) => void
  removeBlendshapeMultiplier: (key: string) => void
  setBlendshapeOffset: (key: string, val: number) => void
  removeBlendshapeOffset: (key: string) => void
  setFaceParam: (key: string, val: number) => void
  removeFaceParam: (key: string) => void
  setEmotion: (key: string, val: number) => void
  removeEmotion: (key: string) => void
  setScalar: (key: string, val: number | string) => void
  touchCheckbox: (key: string) => void
  sendA2FConfig: () => void
}

const A2FSettingsContext = createContext<A2FSettingsContextType>(null!)

export function A2FSettingsProvider({ children }: { children: ReactNode }) {
  const { send } = useWebSocket()
  const [blendshapeMultipliers, setBsMultipliers] = useState<Record<string, number>>({})
  const [blendshapeOffsets, setBsOffsets] = useState<Record<string, number>>({})
  const [faceParams, setFaceParamsState] = useState<Record<string, number>>({})
  const [initialEmotions, setInitialEmotions] = useState<Record<string, number>>({})
  const [scalars, setScalarsState] = useState<Record<string, number | string>>({})
  const [touchedCheckboxes, setTouchedCheckboxes] = useState<Set<string>>(new Set())

  function sendA2FConfig() {
    const config: Record<string, unknown> = {}
    if (Object.keys(blendshapeMultipliers).length) config.blendshapeMultipliers = blendshapeMultipliers
    if (Object.keys(blendshapeOffsets).length) config.blendshapeOffsets = blendshapeOffsets
    if (Object.keys(faceParams).length) config.faceParams = faceParams
    if (Object.keys(initialEmotions).length) config.initialEmotions = initialEmotions

    const scalarNumericFields = ['emotionContrast', 'liveBlendCoef', 'emotionStrength', 'preferredEmotionStrength', 'maxEmotions', 'liveTransitionTime']
    scalarNumericFields.forEach(f => {
      const v = scalars[f]
      if (v !== '' && v !== undefined) config[f] = typeof v === 'string' ? parseFloat(v) : v
    })
    const checkboxFields = ['enableClampingBsWeight', 'enablePreferredEmotion']
    checkboxFields.forEach(f => {
      if (touchedCheckboxes.has(f)) config[f] = !!scalars[f]
    })

    if (Object.keys(config).length === 0) return
    send({ type: 'SetA2FConfig', data: config })
  }

  const setBlendshapeMultiplier = (key: string, val: number) => setBsMultipliers(p => ({ ...p, [key]: val }))
  const removeBlendshapeMultiplier = (key: string) => setBsMultipliers(p => { const n = { ...p }; delete n[key]; return n })
  const setBlendshapeOffset = (key: string, val: number) => setBsOffsets(p => ({ ...p, [key]: val }))
  const removeBlendshapeOffset = (key: string) => setBsOffsets(p => { const n = { ...p }; delete n[key]; return n })
  const setFaceParam = (key: string, val: number) => setFaceParamsState(p => ({ ...p, [key]: val }))
  const removeFaceParam = (key: string) => setFaceParamsState(p => { const n = { ...p }; delete n[key]; return n })
  const setEmotion = (key: string, val: number) => setInitialEmotions(p => ({ ...p, [key]: val }))
  const removeEmotion = (key: string) => setInitialEmotions(p => { const n = { ...p }; delete n[key]; return n })
  const setScalar = (key: string, val: number | string) => setScalarsState(p => ({ ...p, [key]: val }))
  const touchCheckbox = (key: string) => setTouchedCheckboxes(p => new Set([...p, key]))

  return (
    <A2FSettingsContext.Provider value={{
      blendshapeMultipliers, blendshapeOffsets, faceParams, initialEmotions, scalars, touchedCheckboxes,
      setBlendshapeMultiplier, removeBlendshapeMultiplier, setBlendshapeOffset, removeBlendshapeOffset,
      setFaceParam, removeFaceParam, setEmotion, removeEmotion, setScalar, touchCheckbox, sendA2FConfig
    }}>
      {children}
    </A2FSettingsContext.Provider>
  )
}

export const useA2FSettings = () => useContext(A2FSettingsContext)
